import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Bookmark, Trash2, Play } from 'lucide-react';
import { toast } from 'sonner';
import { mediaUrl } from '../utils/mediaUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WatchlistPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchWatchlist();
  }, []);

  const fetchWatchlist = async () => {
    try {
      const { data } = await axios.get(`${API}/watchlist`, { withCredentials: true });
      setItems(data);
    } catch (error) {
      console.error('Error fetching watchlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (movieId) => {
    try {
      await axios.post(`${API}/watchlist/${movieId}`, {}, { withCredentials: true });
      toast.success('Removed from Watch Later');
      fetchWatchlist();
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0F171E' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: '#1E3A8A' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 px-4 sm:px-8 md:px-12 lg:px-16 pb-12" style={{ backgroundColor: '#0F171E' }} data-testid="watchlist-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Bookmark size={32} style={{ color: '#F0C14B' }} fill="#F0C14B" />
          <h1 className="text-4xl font-bold" style={{ color: '#FFFFFF' }}>Watch Later</h1>
        </div>
        <p className="mb-8" style={{ color: '#8197A4' }}>
          {items.length} movie{items.length !== 1 ? 's' : ''} saved to watch later
        </p>

        {items.length === 0 ? (
          <div className="text-center py-16 rounded-2xl" style={{ backgroundColor: '#1A2632', border: '1px dashed #2A3F52' }}>
            <Bookmark size={48} className="mx-auto mb-4" style={{ color: '#8197A4' }} />
            <p className="text-lg font-semibold mb-2" style={{ color: '#FFFFFF' }}>Your watch later list is empty</p>
            <p className="text-sm mb-6" style={{ color: '#8197A4' }}>
              Browse movies and tap the bookmark icon to save them for later
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105"
              style={{ backgroundColor: '#1E3A8A', color: 'white' }}
              data-testid="browse-movies-button"
            >
              Browse Movies
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {items.map((item) => (
              <div
                key={item.watchlist_id}
                className="rounded-lg overflow-hidden relative movie-card-hover cursor-pointer"
                style={{ backgroundColor: '#1A2632' }}
                data-testid={`watchlist-item-${item.movie_id}`}
              >
                <div className="relative" style={{ aspectRatio: '2/3' }} onClick={() => navigate(`/movie/${item.movie_id}`)}>
                  <img
                    src={mediaUrl(item.movie?.poster_path, API) || `https://picsum.photos/seed/${item.movie_id}/400/600`}
                    alt={item.movie?.title}
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(item.movie_id);
                    }}
                    className="absolute top-2 right-2 p-2 rounded-full transition-all duration-200 hover:scale-110"
                    style={{ backgroundColor: 'rgba(220, 38, 38, 0.9)' }}
                    data-testid={`remove-watchlist-${item.movie_id}`}
                    title="Remove from Watch Later"
                  >
                    <Trash2 size={14} color="white" />
                  </button>
                </div>
                <div className="p-3">
                  <h3 className="font-semibold text-sm mb-1 truncate" style={{ color: '#FFFFFF' }}>
                    {item.movie?.title || 'Unknown'}
                  </h3>
                  <div className="flex items-center justify-between text-xs" style={{ color: '#8197A4' }}>
                    <span>{item.movie?.release_year}</span>
                    <span className="px-2 py-1 rounded" style={{ backgroundColor: '#0F171E' }}>
                      {item.movie?.age_rating}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
